import { useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "@tanstack/react-router";
import { Flex, Text } from "styles/utility-components";

import { DetailsContent, DetailsHeader } from "components/app_layout/DetailsLayout";
import Button from "components/ui/button/Button";
import ConfirmModal from "components/ui/modal/ConfirmModal";
import toast from "components/ui/toast/Toast";
import Tooltip from "components/ui/tooltip/Tooltip";
import { useBuyerList } from "lib/hooks/api/buyer_lists/useBuyerList";
import { useDeleteBuyerList } from "lib/hooks/api/buyer_lists/useDeleteBuyerList";
import { useRemoveBuyerEntries } from "lib/hooks/api/buyer_lists/useRemoveBuyerEntries";
import { useDialogManager } from "lib/providers/DialogManager";
import { EventNames, TrackingProvider, useTracking } from "lib/tracking";
import { AddBuyersToListDrawer } from "./AddBuyersToListDrawer";
import BuyerListPermissionsModal, {
  BuyerListPermissionsModalProps,
} from "./BuyerListPermissionsModal";
import { FilterableBuyerTable } from "./FilterableBuyerTable";
import RenameBuyerListModal, { RenameBuyerListModalProps } from "./RenameBuyerListModal";

export function BuyerList({ id }: { id: string }) {
  const { data: list } = useBuyerList(id);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addBuyersDrawerOpen, setAddBuyersDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { logEvent } = useTracking();

  const dialogManager = useDialogManager();

  const { mutate: deleteList } = useDeleteBuyerList({
    onSuccess: () => toast.success("List successfully deleted!"),
    onError: () => toast.error("Sorry, an error has occurred while deleting this list"),
  });

  const { mutate: removeBuyers, isLoading: isRemoveLoading } = useRemoveBuyerEntries({
    onError: () =>
      toast.error(
        `Failed to remove buyers from ${list?.name ?? "buyer list"}, please contact an admin if this issue persists.`,
      ),
  });

  return (
    <TrackingProvider data={{ "List id": id, "List name": list?.name }}>
      <BuyerListHeader>
        <Flex column flexGrow={1} minWidth={0}>
          <Text h1 color="sysTextSecondary" ellipsis title={list?.name}>
            {list?.name}
          </Text>
        </Flex>
        <Flex gap="8px">
          {list && (
            <>
              <Tooltip content="Delete list">
                {/* Span so the tooltip still opens while the button inside is disabled */}
                <span>
                  <Button
                    buttonType="secondary"
                    onClick={() => setDeleteModalOpen(true)}
                    icon="trash03"
                    aria-label="Delete list"
                    disabled={!list.permissionActions.includes("can_delete")}
                  />
                </span>
              </Tooltip>
              <Tooltip content="Rename list">
                <span>
                  <Button
                    buttonType="secondary"
                    onClick={() =>
                      dialogManager.openDialog(RenameBuyerListModal, {
                        list: list,
                      } as Omit<RenameBuyerListModalProps, "isOpen" | "onClose">)
                    }
                    icon="edit02"
                    aria-label="Rename list"
                    disabled={!list.permissionActions.includes("can_update")}
                  />
                </span>
              </Tooltip>
              <Button
                buttonType="secondary"
                onClick={() =>
                  dialogManager.openDialog(BuyerListPermissionsModal, {
                    list: list,
                  } as Omit<BuyerListPermissionsModalProps, "isOpen" | "onClose">)
                }
                icon="share07"
                disabled={!list.permissionActions.includes("can_update")}
              >
                Share
              </Button>
              <Button
                onClick={() => setAddBuyersDrawerOpen(true)}
                icon="plus"
                aria-label="Add buyers"
                disabled={!list.permissionActions.includes("create_buyer_list_entries")}
              >
                Buyer
              </Button>
            </>
          )}
        </Flex>
      </BuyerListHeader>
      <BuyerListContent>
        <FilterableBuyerTable
          defaultFilters={{ buyerLists: [id] }}
          hiddenSections={["buyerLists", "buyerSearch"]}
          hideBulkSave
          extraBulkActions={(selectedIds, clearSelection) =>
            selectedIds.length > 0 && (
              <Tooltip content={`Remove ${selectedIds.length} buyers from ${list?.name ?? "list"}`}>
                <Button
                  buttonType="secondary"
                  loading={isRemoveLoading}
                  onClick={() =>
                    removeBuyers(
                      { id, buyerIds: selectedIds },
                      {
                        onSuccess: () => {
                          clearSelection();
                          toast.success("Buyers removed from list");
                          logEvent(EventNames.buyerBulkRemove, {
                            "Buyer GUIDs": selectedIds,
                            "Buyer list name": list?.name ?? "",
                          });
                        },
                      },
                    )
                  }
                  icon="bookmark"
                  iconProps={{ color: "red" }}
                >
                  Remove
                </Button>
              </Tooltip>
            )
          }
        />
      </BuyerListContent>
      <ConfirmModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onConfirm={() => {
          if (list) {
            deleteList(
              { id: list.id },
              { onSuccess: () => void navigate({ to: "/buyers/lists" }) },
            );
          }
        }}
        message="Are you sure you want to delete this buyer list?"
        confirmLabel="Delete"
      />
      {list && addBuyersDrawerOpen && (
        // Mounted only while open so each opening starts with an empty selection
        <AddBuyersToListDrawer isOpen onClose={() => setAddBuyersDrawerOpen(false)} list={list} />
      )}
    </TrackingProvider>
  );
}

const BuyerListHeader = styled(DetailsHeader)({
  display: "flex",
  alignItems: "center",
  gap: 24,
  padding: 24,
});

const BuyerListContent = styled(DetailsContent)({
  padding: 24,
});
